import type { CSSProperties } from "react";
import { StormcloudVideoPlayer } from "./player/StormcloudVideoPlayer";

export interface OnProgressProps {
  played: number;
  playedSeconds: number;
  loaded: number;
  loadedSeconds: number;
}

export interface BaseStormcloudPlayerProps {
  src?: string;
  playing?: boolean;
  loop?: boolean;
  controls?: boolean;
  volume?: number;
  muted?: boolean;
  playbackRate?: number;
  width?: string | number;
  height?: string | number;
  style?: CSSProperties;
  progressInterval?: number;
  playsInline?: boolean;
  autoplay?: boolean;
  preload?: string;
  poster?: string;
  className?: string;
  wrapperClassName?: string;
  wrapperStyle?: CSSProperties;

  allowNativeHls?: boolean;
  lowLatencyMode?: boolean;
  isLiveStream?: boolean;
  driftToleranceMs?: number;
  immediateManifestAds?: boolean;
  debugAdTiming?: boolean;
  showCustomControls?: boolean;
  hideLoadingIndicator?: boolean;
  licenseKey?: string;
  isVmap?: boolean;
  vmapUrl?: string;
  adFailsafeTimeoutMs?: number;
  minSegmentsBeforePlay?: number;
  ctvAdRequest?: boolean;

  onReady?: (player: StormcloudVideoPlayer) => void;
  onStart?: () => void;
  onPlay?: () => void;
  onPause?: (e?: any) => void;
  onBuffer?: () => void;
  onBufferEnd?: () => void;
  onEnded?: () => void;
  onError?: (
    error: any,
    data?: any,
    hlsInstance?: any,
    hlsGlobal?: any
  ) => void;
  onDuration?: (duration: number) => void;
  onSeek?: (seconds: number) => void;
  onProgress?: (state: OnProgressProps) => void;
  onVolumeToggle?: () => void;
  onFullscreenToggle?: () => void;
  onControlClick?: () => void;

  [otherProps: string]: any;
}

const noop = () => {};

export const defaultProps: Required<
  Omit<
    BaseStormcloudPlayerProps,
    | "src"
    | "onReady"
    | "children"
    | "ctvAdRequest"
    | keyof React.VideoHTMLAttributes<HTMLVideoElement>
  >
> = {
  playing: false,
  loop: false,
  controls: true,
  volume: 1,
  muted: false,
  playbackRate: 1,
  width: "100%",
  height: "auto",
  style: {},
  progressInterval: 1000,
  playsInline: false,
  autoplay: false,
  preload: "metadata",
  poster: "",
  className: "",
  wrapperClassName: "",
  wrapperStyle: {},

  allowNativeHls: false,
  lowLatencyMode: false,
  driftToleranceMs: 1000,
  immediateManifestAds: true,
  debugAdTiming: false,
  showCustomControls: false,
  hideLoadingIndicator: false,
  licenseKey: "",
  isVmap: false,
  vmapUrl: "",
  adFailsafeTimeoutMs: 10000,
  minSegmentsBeforePlay: 2,

  onStart: noop,
  onPlay: noop,
  onPause: noop,
  onBuffer: noop,
  onBufferEnd: noop,
  onEnded: noop,
  onError: noop,
  onDuration: noop,
  onSeek: noop,
  onProgress: noop,
  onVolumeToggle: noop,
  onFullscreenToggle: noop,
  onControlClick: noop,
};
